import React, { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { Routes, Route } from 'react-router-dom';

import { LayoutSpinner } from './App.styled';
import {
  SpinnerWidgetContainer,
  SpinnerFullContainer,
} from '@prometheus/features';
import { Box, Spinner } from '@prometheus/components';
import useStorage from './hooks/useStorage';
import Welcome from './components/Welcome';
import { PrivateRoute } from './components/PrivateRoute';
import { AppLayout } from './components/AppLayout';
import { modulesSideNavigation } from './app.config';
import { useSelector } from 'react-redux';
import { AppState, UserDTO } from '@prometheus/types';
import { healthCheck } from './api';
import { ErrorPage } from './components/ErrorPage';
import './fonts.css';
import './styles.css';

import { ModalProvider } from '@prometheus/hooks';
import { api } from '@prometheus/utils';

enum HealthCheckStatus {
  ALIVE = 'ALIVE',
  RELOAD = 'RELOAD',
}

type RoleObject = {
  id?: unknown;
  name?: unknown;
  title?: unknown;
  value?: unknown;
};

const roleToComparableString = (role: unknown): string => {
  const roleValue =
    role && typeof role === 'object'
      ? (role as RoleObject).id ??
        (role as RoleObject).name ??
        (role as RoleObject).title ??
        (role as RoleObject).value
      : role;

  return String(roleValue)
    .replace(/[_\s-]/g, '')
    .toLowerCase();
};

const resolveBooleanFlag = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.toLowerCase();

    if (normalizedValue === 'true') {
      return true;
    }

    if (normalizedValue === 'false') {
      return false;
    }
  }

  return undefined;
};

const resolveAdminRole = (roles: unknown): boolean | undefined => {
  if (typeof roles === 'boolean') {
    return roles;
  }

  if (Array.isArray(roles)) {
    const resolvedRoles = roles.map(role => resolveAdminRole(role));

    if (resolvedRoles.includes(false)) {
      return false;
    }

    return resolvedRoles.includes(true) || undefined;
  }

  if (typeof roles === 'string') {
    return roleToComparableString(roles).includes('admin') || undefined;
  }

  if (roles && typeof roles === 'object' && 'admin' in roles) {
    return resolveBooleanFlag((roles as { admin?: unknown }).admin);
  }

  if (roles && typeof roles === 'object') {
    return roleToComparableString(roles).includes('admin') || undefined;
  }

  return undefined;
};

const hasAdminRole = (
  user?: (UserDTO & Record<string, any>) | null,
  tenantUser?: Record<string, any> | null
) => {
  const activeTenant = user?.tenants?.find(
    (tenant: { id?: string; object?: { id?: string } }) =>
      tenant.id === user?.activeTenant ||
      tenant.object?.id === user?.activeTenant
  ) as any;

  const adminRole = [
    tenantUser?.roles,
    tenantUser?.admin,
    tenantUser?.object?.roles,
    tenantUser?.object?.admin,
    user?.roles,
    user?.role,
    user?.admin,
    user?.actions,
    activeTenant?.roles,
    activeTenant?.object?.roles,
    activeTenant?.admin,
    activeTenant?.object?.admin,
    activeTenant?.actions,
    activeTenant?.object?.actions,
  ].reduce<boolean | undefined>((result, roles) => {
    return result ?? resolveAdminRole(roles);
  }, undefined);

  if (adminRole !== undefined) {
    return adminRole;
  }

  if (user?.activeTenant) {
    return false;
  }

  return user?.moduleIds?.some(
    (moduleId: string) => roleToComparableString(moduleId) === 'admin'
  );
};

const getTenantUsers = (tenantId: string): Promise<any[]> =>
  api.get(`/users/tenant/${tenantId}`);

export const App: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reloadId, setReloadId] = useState<any>();
  const [tenantUser, setTenantUser] = useState<Record<string, any> | null>();
  const [healthCheckStatus, setHealthCheckStatus] = useState(
    HealthCheckStatus.ALIVE
  );
  const modules = useMemo(() => modulesSideNavigation(), []);
  const user = useSelector<AppState, UserDTO>(state => state.user.data);
  let interval: any = null;
  // THIS LIST SHOULD BE REDONE
  const isAdmin = hasAdminRole(
    user as UserDTO & Record<string, any>,
    tenantUser
  );
  const enabledModuleIds = React.useMemo(
    () => [
      'vocabulary',
      'workspace',
      'study',
      'library',
      'users',
      'datacatalog',
      'documentation',
      ...(user?.moduleIds?.filter(
        (moduleId: string) => roleToComparableString(moduleId) !== 'admin'
      ) || []),
      ...(isAdmin ? ['admin'] : []),
    ],
    [isAdmin, user?.moduleIds]
  );
  const { initStorage, storage } = useStorage();

  const getHealthCheck = () => {
    setHealthCheckStatus(HealthCheckStatus.RELOAD);
  };

  const startCheck = async () => {
    try {
      await healthCheck();
      setHealthCheckStatus(HealthCheckStatus.ALIVE);
    } catch (e) {
      setReloadId(Date.now());
    }
  };

  useEffect(() => {
    if (healthCheckStatus === HealthCheckStatus.RELOAD) {
      setTimeout(() => {
        startCheck();
      }, 7000);
    }
  }, [healthCheckStatus, reloadId]);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  useEffect(() => {
    if (!user?.id || !user?.activeTenant) {
      setTenantUser(null);
      return;
    }

    setTenantUser(undefined);

    getTenantUsers(user.activeTenant)
      .then(users => {
        const currentTenantUser = users.find(
          item =>
            item?.object?.id === user.id ||
            item?.id === user.id ||
            item?.userId === user.id
        );

        setTenantUser(currentTenantUser || null);
      })
      .catch(() => setTenantUser(null));
  }, [user?.activeTenant, user?.id]);

  if (isLoading) {
    return (
      <SpinnerWidgetContainer>
        <LayoutSpinner size={70} />
      </SpinnerWidgetContainer>
    );
  }

  return (
    <>
      {healthCheckStatus === HealthCheckStatus.RELOAD && (
        <SpinnerFullContainer>
          <>
            <Spinner size={70} />
            <Box component="h2" sx={{ marginLeft: '20px' }}>
              The application has been restarted. Please wait for a moment
            </Box>
          </>
        </SpinnerFullContainer>
      )}
      <Routes>
        <Route path="/error/:code" element={<ErrorPage />} />
        <Route path="/*" element={<PrivateRoute />}>
          <Route
            element={
              //  should be inside Private route container, so it disappear after logout
              <ModalProvider>
                <AppLayout
                  modulesSideNavigation={modules.filter(
                    module => module.isMainMenu
                  )}
                  enabledModuleIds={enabledModuleIds}
                />
              </ModalProvider>
            }
          >
            <Route
              index
              element={
                <Welcome
                  modulesSideNavigation={modules}
                  enabledModuleIds={enabledModuleIds}
                />
              }
            />
            {modules.map((mod: any) => {
              const { component: Component, ...rest } = mod;
              const storageModule = storage[rest.name] as any;
              if (enabledModuleIds.includes(rest.name))
                return (
                  <Route
                    path={`${rest.path}/*`}
                    key={rest.name}
                    element={
                      // <ErrorBoundary fallback={<p>Something went wrong <a href='/'>Go to home page</a></p>}>
                      <Component
                        {...rest}
                        storage={storageModule}
                        initStorage={initStorage}
                        getHealthCheck={getHealthCheck}
                      />
                      // </ErrorBoundary>
                    }
                  />
                );
            })}
          </Route>
        </Route>
      </Routes>
    </>
  );
};

export default App;
